import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import Close from '../../../assets/Close';
import SuccessMessage from './components/SuccessMessage';
import PartiesSection from './components/PartiesSection';
import ProductSection from './components/ProductSection';
import ContractValiditySection from './components/ContractValiditySection';
import { ContractsService } from '../../../services/contracts.service';
import ContractSummary from './components/ContractSummary';
import PriceQuantitySection from './components/PriceQuantitySection';
import { toastVariant } from '../../../components/ToastVariant/toastVariant';
import { ContractFormValues } from './addNewContract.interface';

const AddNewContract: React.FC = () => {
	const { t } = useTranslate();
	const navigate = useNavigate();

	const [showSuccess, setShowSuccess] = useState<boolean>(false);
	const [successData, setSuccessData] = useState<{
		buyerName: string;
		sellerName: string;
		productName: string;
		pricePerTonBuyer: number;
		pricePerTonSeller: number;
		markup: number;
		quantity: number;
		dateFrom: string;
		dateTo: string;
		buyerRepresentative: string;
		sellerRepresentative: string;
		buyerPrice: number;
		sellerPrice: number;
		sypacProfit: number;
		storeName?: string;
		storeAddress?: string;
		storeCountryCode?: string;
		countryCode: string;
		allowedOverdraftPercent?: number | string;
	} | null>(null);

	const initialValues: ContractFormValues = {
		buyerCompany: '',
		sellerCompany: '',
		buyerRepresentativeUid: '',
		buyerRepresentativeLabel: '',
		buyerRepresentativePhotoUrl: '',
		sellerRepresentativeUid: '',
		sellerRepresentativeLabel: '',
		sellerRepresentativePhotoUrl: '',
		buyerLabel: '',
		buyerCompanyCountryCode: '',
		buyerCompanyAddress: '',
		buyerCompanyLogoUrl: '',
		sellerLabel: '',
		sellerCompanyLogoUrl: '',
		productName: '',
		productLabel: '',
		productType: '',
		productSize: '',
		pricePerTonSeller: '',
		pricePerTonBuyer: '',
		markup: '',
		quantity: '',
		store: '',
		storeName: '',
		storeAddress: '',
		storeCountryCode: '',
		dateFrom: '',
		dateTo: '',
		countryCode: '',
		allowedOverdraftPercent: '',
		ignoreCreditLimit: false,
	};

	const formik = useFormik<ContractFormValues>({
		initialValues,
		onSubmit: async (values) => {
			try {
				const countryCodeMap: Record<string, string> = { MD: 'MDA', PL: 'POL' };
				const qty = Number(values.quantity) || 0;
				const perTonSeller = Number(values.pricePerTonSeller) || 0;
				const perTonBuyer = Number(values.pricePerTonBuyer) || 0;
				const markup = Number(values.markup) || 0;

				const overdraftRaw = String(
					values.allowedOverdraftPercent ?? '',
				).trim();
				const overdraftParsed =
					overdraftRaw === ''
						? undefined
						: Number(overdraftRaw.replace(',', '.'));
				const allowedOverdraftPercent = Number.isFinite(overdraftParsed)
					? overdraftParsed
					: undefined;

				const payload = {
					buyerId: Number(values.buyerCompany),
					sellerId: Number(values.sellerCompany),
					startsAt: dayjs(values.dateFrom).toISOString(),
					expiresAt: dayjs(values.dateTo).toISOString(),
					countryCode: countryCodeMap[values.countryCode],
					lines: [
						{
							productId: Number(values.productName),
							productName: values.productLabel || '',
							pricePerTonSeller: perTonSeller,
							pricePerTonBuyer: perTonBuyer,
							contractQtyTons: qty,
							markup,
							ignoreCreditLimit: Boolean(values.ignoreCreditLimit),
							...(allowedOverdraftPercent !== undefined
								? { allowedOverdraftPercent }
								: {}),
						},
					],
					buyerRepresentativeUid: values.buyerRepresentativeUid || '',
					sellerRepresentativeUid: values.sellerRepresentativeUid || '',
				};

				await ContractsService.createContract(payload);

				const buyerPrice = perTonBuyer * qty;
				const sellerPrice = perTonSeller * qty;
				const sypacProfit = Math.max(0, sellerPrice - buyerPrice);

				setSuccessData({
					buyerName: values.buyerLabel,
					sellerName: values.sellerLabel,
					productName: values.productLabel,
					pricePerTonBuyer: perTonBuyer,
					pricePerTonSeller: perTonSeller,
					markup,
					quantity: qty,
					dateFrom: values.dateFrom,
					dateTo: values.dateTo,
					buyerRepresentative: values.buyerRepresentativeLabel,
					sellerRepresentative: values.sellerRepresentativeLabel,
					buyerPrice,
					sellerPrice,
					sypacProfit,
					storeName: values.storeName,
					storeAddress: values.storeAddress,
					storeCountryCode: values.storeCountryCode,
					countryCode: values.countryCode,
					allowedOverdraftPercent: values.allowedOverdraftPercent,
				});

				setShowSuccess(true);
			} catch (err) {
				return toastVariant(`Failed to create contract: ${err}`, true);
			}
		},
		validationSchema: Yup.object().shape({
			buyerCompany: Yup.string().required(
				t(
					'adminContracts.buyerCompanyRequired',
					'Please enter the buyer company',
				),
			),
			sellerCompany: Yup.string().required(
				t(
					'adminContracts.sellerCompanyRequired',
					'Please enter the seller company',
				),
			),
			productName: Yup.string().required(
				t('adminContracts.productRequired', 'Please select a product'),
			),
			dateFrom: Yup.string()
				.required(
					t('adminContracts.dateFromRequired', 'Please select start date'),
				)
				.test(
					'date-from-not-in-past',
					t(
						'adminContracts.dateFromTodayOrLater',
						'Start date must be today or later',
					),
					(val) =>
						!!val &&
						dayjs(val).isValid() &&
						dayjs(val).startOf('day').diff(dayjs().startOf('day')) >= 0,
				)
				.test(
					'date-from-before-date-to',
					t(
						'adminContracts.dateFromBeforeDateTo',
						'Start date must be on or before end date',
					),
					function (val) {
						const { dateTo } = this.parent as { dateTo?: string };
						if (!val || !dateTo) return true;
						return (
							dayjs(val).startOf('day').diff(dayjs(dateTo).startOf('day')) <= 0
						);
					},
				),
			dateTo: Yup.string()
				.required(t('adminContracts.dateToRequired', 'Please select end date'))
				.test(
					'date-to-after-from',
					t(
						'adminContracts.dateToAfterFrom',
						'End date must be the same as or after start date',
					),
					function (val) {
						const { dateFrom } = this.parent as { dateFrom: string };
						return (
							!!val &&
							!!dateFrom &&
							dayjs(val).isValid() &&
							dayjs(dateFrom).isValid() &&
							dayjs(val).startOf('day').diff(dayjs(dateFrom).startOf('day')) >=
								0
						);
					},
				),
			quantity: Yup.number()
				.required(t('adminContracts.quantityRequired', 'Please enter quantity'))
				.positive(
					t(
						'adminContracts.quantityGreaterThanZero',
						'Quantity must be greater than 0',
					),
				),
			pricePerTonSeller: Yup.number()
				.required(t('adminContracts.priceRequired'))
				.min(0, t('adminContracts.priceMin', 'Price must be 0 or greater')),
			pricePerTonBuyer: Yup.number()
				.required(t('adminContracts.priceRequired'))
				.min(0, t('adminContracts.priceMin', 'Price must be 0 or greater')),
			buyerRepresentativeUid: Yup.string().required(
				t(
					'adminContracts.selectBuyerRepresentative',
					'Please select a buyer representative',
				),
			),
			sellerRepresentativeUid: Yup.string().required(
				t(
					'adminContracts.selectSellerRepresentative',
					'Please select a seller representative',
				),
			),
			markup: Yup.number()
				.transform((_, originalValue) => {
					const raw = String(originalValue ?? '').trim();

					if (raw === '' || raw === '-') {
						return undefined;
					}

					const normalized = raw.replace(',', '.');
					const parsed = Number(normalized);

					return Number.isNaN(parsed) ? undefined : parsed;
				})
				.required(t('adminContracts.markupRequired', 'Please enter mark-up')),
			allowedOverdraftPercent: Yup.number()
				.transform((_, originalValue) => {
					const raw = String(originalValue ?? '').trim();
					if (raw === '') return undefined;

					const normalized = raw.replace(',', '.');
					const parsed = Number(normalized);

					return Number.isNaN(parsed) ? undefined : parsed;
				})
				.min(0, t('adminContracts.overdraftMin', 'Overdraft must be 0 or more'))
				.max(
					100,
					t('adminContracts.overdraftMax', 'Maximum allowed Overdraft is 100'),
				),
		}),
		validateOnMount: true,
	});

	return (
		<div className="w-full h-full flex gap-5 justify-center">
			{showSuccess && successData ? (
				<SuccessMessage
					{...successData}
					onConfirm={() => {
						sessionStorage.setItem('contractCreated', '1');
						navigate('/contracts');
					}}
				/>
			) : (
				<>
					<div className="max-w-[624px] w-full flex flex-col gap-[30px]">
						<div className="flex flex-col gap-5">
							<div className="flex justify-between items-center">
								<SypacText variant="body-normal-medium">
									<p className="text-[16px] text-gray-60">
										<T keyName="adminContracts.createContract">
											Create contract
										</T>
									</p>
								</SypacText>
								<SypacButton variant="secondary" size="medium">
									<button
										type="button"
										onClick={() => navigate('/contracts')}
										className="p-2 border border-solid border-gray-10 transition hover:border-primary-violet"
									>
										<Close />
									</button>
								</SypacButton>
							</div>

							<SypacText variant="body-normal-medium">
								<p className="text-xl text-gray-80">
									<T keyName="adminContracts.contractDetails">
										Contract details
									</T>
								</p>
							</SypacText>
						</div>

						<div className="h-[calc(100%-52px)] border border-solid border-gray-10 rounded-[12px] overflow-hidden">
							<form
								onSubmit={formik.handleSubmit}
								autoComplete="off"
								className="h-full flex flex-col"
							>
								<div className="flex-1 flex flex-col gap-3 overflow-y-scroll scrollbar-custom scroll-smooth pb-3">
									<PartiesSection formik={formik} />

									<div className="border-0 border-solid border-t-[1px] border-gray-10" />

									<ProductSection formik={formik} />

									<div className="border-0 border-solid border-t-[1px] border-gray-10" />

									<PriceQuantitySection formik={formik} />

									<div className="border-0 border-solid border-t-[1px] border-gray-10" />

									<ContractValiditySection formik={formik} />
								</div>
								<nav className="z-10 flex justify-center gap-3 p-3 bg-white border border-solid border-b-0 rounded-10 border-gray-10 -mx-[1px]">
									<SypacButton
										variant="secondary"
										size="small"
										className="w-full"
									>
										<button
											type="button"
											className="w-full px-6 py-[10px] rounded-lg"
											onClick={() => navigate('/contracts')}
										>
											<SypacText variant="body-regular-medium">
												<p>
													<T keyName="modalDownload.close">Close</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
									<SypacButton
										variant="primary"
										size="small"
										className="w-full"
									>
										<button
											type="submit"
											className="w-full px-6 py-[11px] rounded-lg"
										>
											<SypacText
												variant="body-regular-medium"
												className="flex items-center gap-4"
											>
												<p>
													<T keyName="adminContracts.createContract">
														Create contract
													</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
								</nav>
							</form>
						</div>
					</div>

					<ContractSummary formik={formik} />
				</>
			)}
		</div>
	);
};

export default AddNewContract;
